import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Row, Col, Alert } from 'react-bootstrap';

const UserFormModal = ({
  show,
  handleClose,
  mode,
  userData,
  onSave,
  userRole,
  showLimitField = false,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    status: 'Active',
    joinDate: new Date().toISOString().split('T')[0],
    limit: '',
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const cities = [
    'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Chennai', 
    'Kolkata', 'Pune', 'Ahmedabad', 'Jaipur', 'Lucknow',
    'Surat', 'Chandigarh', 'Indore', 'Nagpur', 'Vadodara'
  ];

  // Load user data when editing
  useEffect(() => {
    if (mode === 'edit' && userData) {
      setFormData({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
        city: userData.city || '',
        status: userData.status || 'Active',
        joinDate: userData.joinDate || new Date().toISOString().split('T')[0],
        limit: showLimitField ? userData.limit ?? '' : '',
      });
    } else if (mode === 'add') {
      // Reset form for add mode
      setFormData({
        name: '',
        email: '',
        phone: '',
        city: '',
        status: 'Active',
        joinDate: new Date().toISOString().split('T')[0],
        limit: '',
      });
    }
    setErrors({});
  }, [mode, userData, show]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    // Phone validation
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else {
      // Remove spaces and +91 prefix for validation
      const phoneDigits = formData.phone.replace(/[\s+]/g, '').replace(/^91/, '');
      if (!/^\d{10}$/.test(phoneDigits)) {
        newErrors.phone = 'Phone number must be 10 digits';
      }
    }

    // City validation
    if (!formData.city) {
      newErrors.city = 'City is required';
    }

    // Date validation
    if (!formData.joinDate) {
      newErrors.joinDate = 'Join date is required';
    }

    if (showLimitField) {
      if (formData.limit === '' || formData.limit === null) {
        newErrors.limit = 'Limit is required';
      } else if (Number(formData.limit) < 0) {
        newErrors.limit = 'Limit must be positive';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Format phone number with +91 if not already present
    let formattedPhone = formData.phone.trim();
    if (!formattedPhone.startsWith('+91')) {
      const phoneDigits = formattedPhone.replace(/[\s+]/g, '').replace(/^91/, '');
      formattedPhone = `+91 ${phoneDigits}`;
    }

    const dataToSave = {
      ...formData,
      phone: formattedPhone,
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
    };

    if (showLimitField) {
      dataToSave.limit = Number(formData.limit);
    }

    // If editing, include the ID
    if (mode === 'edit' && userData) {
      dataToSave.id = userData.id;
    }

    try {
      await onSave(dataToSave);
      handleModalClose();
    } catch (error) {
      console.error('Error saving user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle modal close
  const handleModalClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      city: '',
      status: 'Active',
      joinDate: new Date().toISOString().split('T')[0],
      limit: '',
    });
    setErrors({});
    setIsSubmitting(false);
    handleClose();
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className={`bi ${mode === 'add' ? 'bi-person-plus' : 'bi-pencil-square'} me-2`}></i>
          {mode === 'add' ? `Add New ${userRole}` : `Edit ${userRole}`}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            {/* Name Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter full name"
                  isInvalid={!!errors.name}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Email Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  isInvalid={!!errors.email}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Phone Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Phone Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+91 9876543210"
                  isInvalid={!!errors.phone}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Enter 10-digit mobile number
                </Form.Text>
              </Form.Group>
            </Col>

            {/* City Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  City <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  isInvalid={!!errors.city}
                  disabled={isSubmitting}
                >
                  <option value="">Select City</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.city}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {/* Status Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Status <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>

            {/* Join Date Field */}
            <Col md={6} className="mb-3">
              <Form.Group>
                <Form.Label>
                  Join Date <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="date"
                  name="joinDate"
                  value={formData.joinDate}
                  onChange={handleChange}
                  isInvalid={!!errors.joinDate}
                  disabled={isSubmitting}
                  max={new Date().toISOString().split('T')[0]}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.joinDate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {showLimitField && (
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>
                    Limit (₹) <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="limit"
                    value={formData.limit}
                    onChange={handleChange}
                    placeholder="Enter credit limit"
                    min="0"
                    step="1"
                    isInvalid={!!errors.limit}
                    disabled={isSubmitting}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.limit}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            )}
          </Row>
        </Modal.Body>

        <Modal.Footer style={{ borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa' }}>
          <Button 
            type="submit"
            disabled={isSubmitting}
            className="btn-outline-primary-custom"
          >
            {isSubmitting ? 'Saving...' : mode === 'add' ? 'Add User' : 'Update User'}
          </Button>
          <Button 
            variant="secondary" 
            onClick={handleModalClose}
            disabled={isSubmitting}
            className="btn-outline-danger-custom"
          >
            Cancel
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UserFormModal;

